import React, { useState, useEffect } from 'react';
import {
  getStudents,
  getStudent,
  addStudent,
  updateStudent,
  deleteStudent,
  bulkUpdateStudents,
  bulkDeleteStudents,
  exportStudents,
  getCampuses,
  getCourses,
  getLevels
} from '../api';
import { usePermissions } from '../utils/permissions';

function Students() {
  const { can } = usePermissions();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [courses, setCourses] = useState([]);
  const [levels, setLevels] = useState([]);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [bulkUpdateData, setBulkUpdateData] = useState({
    campus_id: '',
    course_id: '',
    course_level: '',
    year: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0
  });
  const [filters, setFilters] = useState({
    campus: '',
    course: '',
    level: '',
    year: '',
    search: '',
    startDate: '',
    endDate: '',
    gender: '',
    session: '',
    createdFrom: '',
    createdTo: ''
  });
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [formData, setFormData] = useState({
    name: '',
    father_name: '',
    campus_id: '',
    cnic: '',
    passport_number: '',
    contact_no: '',
    email: '',
    address: '',
    qualification: '',
    program: '',
    gender: '',
    date_of_birth: '',
    session: '',
    course_id: '',
    course_level: '',
    start_date: '',
    end_date: '',
    year: new Date().getFullYear()
  });

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchStudents();
  }, [pagination.page, filters, sortField, sortOrder]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [campusesRes, coursesRes, levelsRes] = await Promise.all([
        getCampuses(),
        getCourses(),
        getLevels()
      ]);
      setCampuses(campusesRes.data);
      setCourses(coursesRes.data);
      setLevels(levelsRes.data);
    } catch (error) {
      showMessage('error', 'Failed to load data');
    }
  };

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: 50,
        ...filters,
        sortField,
        sortOrder
      };
      const response = await getStudents(params);
      
      // Apply client-side sorting if sortField is set
      let sortedStudents = response.data.students;
      if (sortField) {
        sortedStudents = [...response.data.students].sort((a, b) => {
          let aVal = a[sortField];
          let bVal = b[sortField];
          
          // Handle null/undefined values
          if (aVal === null || aVal === undefined) return 1;
          if (bVal === null || bVal === undefined) return -1;
          
          // Handle date fields
          if (sortField.includes('date') || sortField === 'year') {
            aVal = new Date(aVal).getTime();
            bVal = new Date(bVal).getTime();
          }
          
          // Handle numeric fields
          if (sortField === 'certificate_no' || sortField === 'cnic') {
            aVal = String(aVal);
            bVal = String(bVal);
          }
          
          // String comparison
          if (typeof aVal === 'string') {
            aVal = aVal.toLowerCase();
            bVal = String(bVal).toLowerCase();
          }
          
          if (sortOrder === 'asc') {
            return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
          } else {
            return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
          }
        });
      }
      
      setStudents(sortedStudents);
      setPagination({
        page: response.data.page,
        totalPages: response.data.totalPages,
        total: response.data.total
      });
      setSelectedStudents([]);
    } catch (error) {
      showMessage('error', 'Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      father_name: '',
      campus_id: '',
      cnic: '',
      passport_number: '',
      contact_no: '',
      address: '',
      qualification: '',
      program: '',
      gender: '',
      date_of_birth: '',
      session: '',
      course_id: '',
      course_level: '',
      start_date: '',
      end_date: '',
      year: new Date().getFullYear()
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingStudent) {
        await updateStudent(editingStudent.id, formData);
        showMessage('success', 'Student updated successfully');
      } else {
        await addStudent(formData);
        showMessage('success', 'Student added successfully');
      }
      setShowModal(false);
      resetForm();
      setEditingStudent(null);
      fetchStudents();
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Operation failed');
    }
  };

  const handleEdit = async (id) => {
    try {
      const response = await getStudent(id);
      const student = response.data;
      setEditingStudent(student);
      setFormData({
        name: student.name || '',
        father_name: student.father_name || '',
        campus_id: student.campus_id || '',
        cnic: student.cnic || '',
        passport_number: student.passport_number || '',
        contact_no: student.contact_no || '',
        address: student.address || '',
        qualification: student.qualification || '',
        program: student.program || '',
        gender: student.gender || '',
        date_of_birth: student.date_of_birth ? student.date_of_birth.split('T')[0] : '',
        session: student.session || '',
        course_id: student.course_id || '',
        course_level: student.course_level || '',
        start_date: student.start_date ? student.start_date.split('T')[0] : '',
        end_date: student.end_date ? student.end_date.split('T')[0] : '',
        year: student.year || new Date().getFullYear()
      });
      setShowModal(true);
    } catch (error) {
      showMessage('error', 'Failed to load student details');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this student?')) {
      try {
        await deleteStudent(id);
        showMessage('success', 'Student deleted successfully');
        fetchStudents();
      } catch (error) {
        showMessage('error', 'Failed to delete student');
      }
    }
  };

  const openAddModal = () => {
    setEditingStudent(null);
    resetForm();
    setShowModal(true);
  };

  const handleExport = async () => {
    try {
      // Pass current filters to export API
      const response = await exportStudents(filters);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'students_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      showMessage('success', 'Data exported successfully');
    } catch (error) {
      showMessage('error', 'Failed to export data');
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters({ ...filters, [field]: value });
    setPagination({ ...pagination, page: 1 });
  };

  const clearFilters = () => {
    setFilters({
      campus: '',
      course: '',
      level: '',
      year: '',
      search: '',
      startDate: '',
      endDate: '',
      gender: '',
      session: '',
      createdFrom: '',
      createdTo: ''
    });
    setPagination({ ...pagination, page: 1 });
  };

  const toggleSelectAll = () => {
    if (selectedStudents.length === students.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(students.map(s => s.id));
    }
  };

  const toggleSelectStudent = (id) => {
    if (selectedStudents.includes(id)) {
      setSelectedStudents(selectedStudents.filter(sid => sid !== id));
    } else {
      setSelectedStudents([...selectedStudents, id]);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle sort order
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return ' ⇅';
    return sortOrder === 'asc' ? ' ↑' : ' ↓';
  };

  const handleBulkUpdate = async () => {
    if (selectedStudents.length === 0) {
      showMessage('error', 'Please select students to update');
      return;
    }

    const updateData = {};
    if (bulkUpdateData.campus_id) updateData.campus_id = bulkUpdateData.campus_id;
    if (bulkUpdateData.course_id) updateData.course_id = bulkUpdateData.course_id;
    if (bulkUpdateData.course_level) updateData.course_level = bulkUpdateData.course_level;
    if (bulkUpdateData.year) updateData.year = bulkUpdateData.year;

    if (Object.keys(updateData).length === 0) {
      showMessage('error', 'Please select at least one field to update');
      return;
    }

    try {
      await bulkUpdateStudents(selectedStudents, updateData);
      setShowBulkModal(false);
      setBulkUpdateData({ campus_id: '', course_id: '', course_level: '', year: '' });
      setSelectedStudents([]);
      await fetchStudents();
      showMessage('success', `${selectedStudents.length} students updated successfully`);
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Bulk update failed');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedStudents.length === 0) {
      showMessage('error', 'Please select students to delete');
      return;
    }

    if (window.confirm(`Are you sure you want to delete ${selectedStudents.length} selected students?`)) {
      try {
        await bulkDeleteStudents(selectedStudents);
        showMessage('success', `${selectedStudents.length} students deleted successfully`);
        fetchStudents();
      } catch (error) {
        showMessage('error', error.response?.data?.error || 'Bulk delete failed');
      }
    }
  };

  if (loading && students.length === 0) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Students ({pagination.total})</h2>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {selectedStudents.length > 0 && (
              <>
                {can('students', 'edit') && (
                  <button className="btn btn-warning" onClick={() => setShowBulkModal(true)}>
                    Bulk Update ({selectedStudents.length})
                  </button>
                )}
                {can('students', 'delete') && (
                  <button className="btn btn-danger" onClick={handleBulkDelete}>
                    Bulk Delete ({selectedStudents.length})
                  </button>
                )}
              </>
            )}
            {can('students', 'view') && (
              <button className="btn btn-success" onClick={handleExport}>
                Export CSV
              </button>
            )}
            {can('students', 'create') && (
              <button className="btn btn-primary" onClick={openAddModal}>
                Add Student
              </button>
            )}
          </div>
        </div>

        {message.text && (
          <div className={`alert alert-${message.type}`}>
            {message.text}
          </div>
        )}

        {/* Filters */}
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ marginBottom: '15px', fontSize: '16px', color: '#333' }}>Filter Students</h3>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Search</label>
              <input
                type="text"
                className="form-input"
                placeholder="Name, CNIC, Certificate No, Contact..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Campus</label>
              <select
                className="form-select"
                value={filters.campus}
                onChange={(e) => handleFilterChange('campus', e.target.value)}
              >
                <option value="">All Campuses</option>
                {campuses.map((campus) => (
                  <option key={campus.id} value={campus.id}>
                    {campus.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Course</label>
              <select
                className="form-select"
                value={filters.course}
                onChange={(e) => handleFilterChange('course', e.target.value)}
              >
                <option value="">All Courses</option>
                {courses.map((course) => (
                  <option key={course.id} value={course.id}>
                    {course.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Level</label>
              <select
                className="form-select"
                value={filters.level}
                onChange={(e) => handleFilterChange('level', e.target.value)}
              >
                <option value="">All Levels</option>
                {levels.map((level) => (
                  <option key={level.id} value={level.id}>
                    Level {level.level}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Gender</label>
              <select
                className="form-select"
                value={filters.gender}
                onChange={(e) => handleFilterChange('gender', e.target.value)}
              >
                <option value="">All</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Session</label>
              <input
                type="text"
                className="form-input"
                placeholder="Session"
                value={filters.session}
                onChange={(e) => handleFilterChange('session', e.target.value)}
              />
            </div>
          </div>

          <div className="form-row" style={{ marginTop: '10px' }}>
            <div className="form-group">
              <label className="form-label">Course Start Date (From)</label>
              <input
                type="date"
                className="form-input"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Course End Date (To)</label>
              <input
                type="date"
                className="form-input"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Record Created (From)</label>
              <input
                type="date"
                className="form-input"
                value={filters.createdFrom}
                onChange={(e) => handleFilterChange('createdFrom', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Record Created (To)</label>
              <input
                type="date"
                className="form-input"
                value={filters.createdTo}
                onChange={(e) => handleFilterChange('createdTo', e.target.value)}
              />
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={clearFilters} style={{ width: '100%' }}>
                Clear All
              </button>
            </div>
          </div>
        </div>

        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={students.length > 0 && selectedStudents.length === students.length}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th onClick={() => handleSort('certificate_no')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                  Cert No{getSortIcon('certificate_no')}
                </th>
                <th onClick={() => handleSort('name')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                  Name{getSortIcon('name')}
                </th>
                <th onClick={() => handleSort('father_name')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                  Father Name{getSortIcon('father_name')}
                </th>
                <th onClick={() => handleSort('campus_name')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                  Campus{getSortIcon('campus_name')}
                </th>
                <th onClick={() => handleSort('course_name')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                  Course{getSortIcon('course_name')}
                </th>
                <th onClick={() => handleSort('level_name')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                  Level{getSortIcon('level_name')}
                </th>
                <th onClick={() => handleSort('cnic')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                  CNIC{getSortIcon('cnic')}
                </th>
                <th>Passport</th>
                <th>Contact</th>
                <th>Email</th>
                <th onClick={() => handleSort('gender')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                  Gender{getSortIcon('gender')}
                </th>
                <th onClick={() => handleSort('date_of_birth')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                  DOB{getSortIcon('date_of_birth')}
                </th>
                <th>Address</th>
                <th>Qualification</th>
                <th>Program</th>
                <th onClick={() => handleSort('session')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                  Session{getSortIcon('session')}
                </th>
                <th onClick={() => handleSort('start_date')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                  Start Date{getSortIcon('start_date')}
                </th>
                <th onClick={() => handleSort('end_date')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                  End Date{getSortIcon('end_date')}
                </th>
                <th onClick={() => handleSort('created_at')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                  Created At{getSortIcon('created_at')}
                </th>
                <th onClick={() => handleSort('updated_at')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                  Updated At{getSortIcon('updated_at')}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {students.length === 0 ? (
                <tr>
                  <td colSpan="23" style={{ textAlign: 'center', padding: '20px' }}>
                    No students found
                  </td>
                </tr>
              ) : (
                students.map((student) => (
                  <tr key={student.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedStudents.includes(student.id)}
                        onChange={() => toggleSelectStudent(student.id)}
                      />
                    </td>
                    <td><strong>{student.certificate_no || '-'}</strong></td>
                    <td><strong>{student.name}</strong></td>
                    <td>{student.father_name || '-'}</td>
                    <td>{student.campus_name || '-'}</td>
                    <td>{student.course_name || '-'}</td>
                    <td>{student.level_number || '-'}</td>
                    <td>{student.cnic || '-'}</td>
                    <td>{student.passport_number || '-'}</td>
                    <td>{student.contact_no || '-'}</td>
                    <td>{student.email || '-'}</td>
                    <td>{student.gender || '-'}</td>
                    <td>{student.date_of_birth ? new Date(student.date_of_birth).toLocaleDateString() : '-'}</td>
                    <td>{student.address || '-'}</td>
                    <td>{student.qualification || '-'}</td>
                    <td>{student.program || '-'}</td>
                    <td>{student.session || '-'}</td>
                    <td>{student.start_date ? new Date(student.start_date).toLocaleDateString() : '-'}</td>
                    <td>{student.end_date ? new Date(student.end_date).toLocaleDateString() : '-'}</td>
                    <td>{student.created_at ? new Date(student.created_at).toLocaleString() : '-'}</td>
                    <td>{student.updated_at ? new Date(student.updated_at).toLocaleString() : '-'}</td>
                    <td>
                      <div className="action-buttons">
                        {can('students', 'edit') && (
                          <button
                            className="btn btn-small btn-secondary"
                            onClick={() => handleEdit(student.id)}
                          >
                            Edit
                          </button>
                        )}
                        {can('students', 'delete') && (
                          <button
                            className="btn btn-small btn-danger"
                            onClick={() => handleDelete(student.id)}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="pagination">
            <button
              onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
              disabled={pagination.page === 1}
            >
              Previous
            </button>
            <span>
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button
              onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
              disabled={pagination.page === pagination.totalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {editingStudent ? 'Edit Student' : 'Add Student'}
              </h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                &times;
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Father Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.father_name}
                    onChange={(e) => setFormData({ ...formData, father_name: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Campus *</label>
                  <select
                    className="form-select"
                    value={formData.campus_id}
                    onChange={(e) => setFormData({ ...formData, campus_id: e.target.value })}
                    required
                  >
                    <option value="">Select Campus</option>
                    {campuses.map((campus) => (
                      <option key={campus.id} value={campus.id}>
                        {campus.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Course *</label>
                  <select
                    className="form-select"
                    value={formData.course_id}
                    onChange={(e) => setFormData({ ...formData, course_id: e.target.value })}
                    required
                  >
                    <option value="">Select Course</option>
                    {courses.map((course) => (
                      <option key={course.id} value={course.id}>
                        {course.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Course Level *</label>
                  <select
                    className="form-select"
                    value={formData.course_level}
                    onChange={(e) => setFormData({ ...formData, course_level: e.target.value })}
                    required
                  >
                    <option value="">Select Level</option>
                    {levels.map((level) => (
                      <option key={level.id} value={level.id}>
                        Level {level.level} - {level.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Year *</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">CNIC</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.cnic}
                    onChange={(e) => setFormData({ ...formData, cnic: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Passport Number</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.passport_number}
                    onChange={(e) => setFormData({ ...formData, passport_number: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Contact No</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.contact_no}
                    onChange={(e) => setFormData({ ...formData, contact_no: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-input"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Gender</label>
                  <select
                    className="form-select"
                    value={formData.gender}
                    onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Date of Birth</label>
                  <input
                    type="date"
                    className="form-input"
                    value={formData.date_of_birth}
                    onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Address</label>
                <textarea
                  className="form-textarea"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Qualification</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.qualification}
                    onChange={(e) => setFormData({ ...formData, qualification: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Program</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.program}
                    onChange={(e) => setFormData({ ...formData, program: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Session</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.session}
                    onChange={(e) => setFormData({ ...formData, session: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Year</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Start Date</label>
                  <input
                    type="date"
                    className="form-input"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">End Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                />
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingStudent ? 'Update' : 'Add'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Update Modal */}
      {showBulkModal && (
        <div className="modal-overlay" onClick={() => setShowBulkModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                Bulk Update {selectedStudents.length} Students
              </h3>
              <button className="modal-close" onClick={() => setShowBulkModal(false)}>
                &times;
              </button>
            </div>
            <div style={{ padding: '20px' }}>
              <p style={{ marginBottom: '20px', color: '#666' }}>
                Select the fields you want to update. Leave blank to keep existing values.
              </p>
              <div className="form-group">
                <label className="form-label">Campus</label>
                <select
                  className="form-select"
                  value={bulkUpdateData.campus_id}
                  onChange={(e) => setBulkUpdateData({ ...bulkUpdateData, campus_id: e.target.value })}
                >
                  <option value="">-- Keep existing --</option>
                  {campuses.map((campus) => (
                    <option key={campus.id} value={campus.id}>
                      {campus.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Course</label>
                <select
                  className="form-select"
                  value={bulkUpdateData.course_id}
                  onChange={(e) => setBulkUpdateData({ ...bulkUpdateData, course_id: e.target.value })}
                >
                  <option value="">-- Keep existing --</option>
                  {courses.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Course Level</label>
                <select
                  className="form-select"
                  value={bulkUpdateData.course_level}
                  onChange={(e) => setBulkUpdateData({ ...bulkUpdateData, course_level: e.target.value })}
                >
                  <option value="">-- Keep existing --</option>
                  {levels.map((level) => (
                    <option key={level.id} value={level.id}>
                      Level {level.level} - {level.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Year</label>
                <input
                  type="number"
                  className="form-input"
                  placeholder="Leave blank to keep existing"
                  value={bulkUpdateData.year}
                  onChange={(e) => setBulkUpdateData({ ...bulkUpdateData, year: e.target.value })}
                  min="2000"
                  max="2100"
                />
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowBulkModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={handleBulkUpdate}>
                  Update Students
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Students;
